"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { fetchPlans, setDiscount } from "../../../services/billingService";
import { rupeesToPaise, formatPaise } from "../../../utils/billingFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ApplyDiscountModal = ({ isOpen, onClose, onSaved, schoolId }) => {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [discountRupees, setDiscountRupees] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPlanId("");
    setDiscountRupees("");
    setReason("");
    fetchPlans({ activeOnly: true })
      .then((res) => setPlans(res.data || []))
      .catch(() => setPlans([]));
  }, [isOpen]);

  const selectedPlan = plans.find((p) => p._id === planId);
  const discountPaise = rupeesToPaise(discountRupees);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!planId) {
      toast.error("Select a plan");
      return;
    }
    if (!discountPaise || discountPaise <= 0) {
      toast.error("Enter a discount amount greater than 0");
      return;
    }
    if (selectedPlan && discountPaise > selectedPlan.baseAmount) {
      toast.error("Discount cannot exceed the plan price");
      return;
    }
    setSubmitting(true);
    try {
      await setDiscount(schoolId, { planId, discountAmount: discountPaise, reason: reason.trim() || undefined });
      toast.success("Discount applied");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Apply Discount</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Plan *</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan._id} value={plan._id}>
                    {plan.name} — {formatPaise(plan.baseAmount)} / {plan.durationMonths}mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Discount Amount (₹) *</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={discountRupees}
              onChange={(e) => setDiscountRupees(e.target.value)}
              placeholder="e.g. 2000"
            />
            {selectedPlan && discountPaise > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {formatPaise(selectedPlan.baseAmount)} − {formatPaise(discountPaise)} ={" "}
                {formatPaise(selectedPlan.baseAmount - discountPaise)} before GST
              </p>
            )}
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Apply Discount"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyDiscountModal;
